import { useState, useEffect } from 'react';
import {
  UserPlus, Search, Edit2, Trash2, Loader2, AlertCircle, X, Eye, EyeOff,
  Check, Copy, KeyRound, RefreshCw, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const generateTemporaryPassword = () => {
  const groups = [
    'ABCDEFGHJKLMNPQRSTUVWXYZ',
    'abcdefghijkmnopqrstuvwxyz',
    '23456789',
    '!@#$%&*?',
  ];
  const allCharacters = groups.join('');
  const randomIndex = (length) => {
    const randomValue = new Uint32Array(1);
    window.crypto.getRandomValues(randomValue);
    return randomValue[0] % length;
  };

  const characters = groups.map(group => group[randomIndex(group.length)]);
  while (characters.length < 12) {
    characters.push(allCharacters[randomIndex(allCharacters.length)]);
  }
  for (let index = characters.length - 1; index > 0; index -= 1) {
    const swapIndex = randomIndex(index + 1);
    [characters[index], characters[swapIndex]] = [characters[swapIndex], characters[index]];
  }
  return characters.join('');
};

export const UsersPage = () => {
  const perms = detectPerms();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');

  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '', role_id: '', active: true
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/roles`, getAuthHeaders()),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (err) { setError('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredUsers = users.filter(u =>
    !searchQuery ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openAdd = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', phone: '', role_id: '', active: true });
    setError(''); setShowPassword(false); setCreatedCredentials(null); setCopied(false); setCopyError(''); setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name, email: user.email, password: '', phone: user.phone || '',
      role_id: user.role_id || '', active: user.active
    });
    setError(''); setShowPassword(false); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    try {
      if (editingUser) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await axios.put(`${BACKEND_URL}/api/users/${editingUser.id}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/users`, formData, getAuthHeaders());
        setCreatedCredentials({
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
        });
        setCopied(false);
        setCopyError('');
      }
      setShowModal(false);
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save user'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchData();
    } catch (err) { setError('Failed to delete user'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const onboardingMessage = createdCredentials ? [
    `Hello ${createdCredentials.name},`,
    '',
    'Your account has been created on the Tejas Portal. Please use the credentials below to sign in:',
    '',
    `Portal: ${window.location.origin}/admin/login`,
    `Email: ${createdCredentials.email}`,
    `Temporary password: ${createdCredentials.password}`,
    '',
    'For your security, you will be required to create a new password when you sign in for the first time. Please keep these credentials confidential and do not share them with anyone.',
    '',
    'Regards,',
    'Tejas Info Solutions',
  ].join('\n') : '';

  const copyOnboardingMessage = async () => {
    try {
      await navigator.clipboard.writeText(onboardingMessage);
      setCopyError('');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopyError('Could not copy the login message. Please copy the details manually.');
    }
  };

  const roleColor = (roleName) => {
    const colors = {
      'Superadmin': 'bg-red-100 text-red-700',
      'Admin': 'bg-orange-100 text-orange-700',
      'Accounts': 'bg-blue-100 text-blue-700',
      'Inventory Manager': 'bg-emerald-100 text-emerald-700',
      'Sales Manager': 'bg-purple-100 text-purple-700',
      'Service Manager': 'bg-cyan-100 text-cyan-700',
      'Service Technician': 'bg-yellow-100 text-yellow-700',
    };
    return colors[roleName] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Users</h1>
          <p className="text-slate-500 mt-1">Manage users and role assignments</p>
        </div>
        <button onClick={openAdd} data-testid="add-user-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-6 py-2.5 rounded-none hover:bg-orange-600 transition-colors">
          <UserPlus className="h-4 w-4" /> Add User
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" placeholder="Search by name, email, or role..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            data-testid="user-search"
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-3">Name</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-3">Email</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Role</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Phone</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Status</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Actions</div>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No users found</div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user.id} data-testid={`user-row-${user.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="col-span-3 font-medium text-slate-900">{user.name}</div>
                <div className="col-span-3 text-slate-600 text-sm">{user.email}</div>
                <div className="col-span-2">
                  {user.role_name ? (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${roleColor(user.role_name)}`}>
                      {user.role_name}
                    </span>
                  ) : <span className="text-slate-400 text-sm">-</span>}
                </div>
                <div className="text-slate-600 text-sm">{user.phone || '-'}</div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  {perms.canEdit && (
                  <button onClick={() => openEdit(user)} data-testid={`edit-user-${user.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors" title="Edit">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  )}
                  {perms.canDelete && (
                  <button onClick={() => setDeleteConfirm(user.id)} data-testid={`delete-user-${user.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete User?</h3>
            <p className="text-slate-600 mb-4 text-sm">This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-600 text-white font-bold rounded-sm hover:bg-red-700">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingUser ? 'Edit User' : 'Add User'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    required data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email <span className="text-red-500">*</span></label>
                  <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    required data-testid="input-email"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {editingUser ? 'Password' : 'Temporary Password'} {!editingUser && <span className="text-red-500">*</span>}
                    {editingUser && <span className="text-xs text-slate-400 ml-1">(leave blank to keep current)</span>}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <input type={showPassword ? 'text' : 'password'} value={formData.password}
                        onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                        required={!editingUser} minLength={editingUser ? undefined : 8} autoComplete="new-password"
                        data-testid="input-password"
                        className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {!editingUser && (
                      <button type="button" data-testid="generate-password-btn"
                        onClick={() => {
                          setFormData(previous => ({ ...previous, password: generateTemporaryPassword() }));
                          setShowPassword(true);
                        }}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100 text-sm font-semibold whitespace-nowrap">
                        <RefreshCw className="h-4 w-4" /> Generate
                      </button>
                    )}
                  </div>
                  {!editingUser && <p className="text-xs text-slate-500 mt-1">The user will be required to replace this password on first login.</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="text" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                    data-testid="input-phone"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select value={formData.role_id} onChange={e => setFormData(p => ({ ...p, role_id: e.target.value }))}
                    data-testid="input-role"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">-- Select Role --</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <div className="text-sm font-medium text-slate-700">Active</div>
                  <button type="button" onClick={() => setFormData(p => ({ ...p, active: !p.active }))}
                    data-testid="toggle-active"
                    className={`relative w-11 h-6 rounded-full transition-colors ${formData.active ? 'bg-orange-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.active ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-user-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Newly Created User Credentials */}
      {createdCredentials && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-sm w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-green-100 text-green-700 flex items-center justify-center rounded-full">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">User Created</h2>
                  <p className="text-sm text-slate-500">Share these temporary login details securely.</p>
                </div>
              </div>
              <button onClick={() => setCreatedCredentials(null)} className="p-2 text-slate-400 hover:text-slate-600" aria-label="Close credentials modal">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {copyError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" /> {copyError}
                </div>
              )}
              <div className="bg-slate-50 border border-slate-200 divide-y divide-slate-200 mb-4">
                <div className="px-4 py-3">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Email</p>
                  <p className="font-semibold text-slate-900 mt-1 break-all" data-testid="created-user-email">{createdCredentials.email}</p>
                </div>
                <div className="px-4 py-3">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Temporary Password</p>
                  <p className="font-mono font-semibold text-slate-900 mt-1 break-all" data-testid="created-user-password">{createdCredentials.password}</p>
                </div>
              </div>

              <div className="flex gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm mb-6">
                <KeyRound className="h-5 w-5 flex-shrink-0" />
                <p>This password is displayed only for sharing now. The user must change it on first login.</p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button type="button" onClick={() => setCreatedCredentials(null)}
                  className="px-5 py-2.5 border border-slate-200 text-slate-700 hover:bg-slate-50">
                  Close
                </button>
                <button type="button" onClick={copyOnboardingMessage} data-testid="copy-login-message-btn"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-orange-500 text-white font-bold hover:bg-orange-600">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Message Copied' : 'Share — Copy Message'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
